import { api, cacheTimer } from "@app/api";
import { defaultEntity, defaultHalHref } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  InstanceClass,
  LinkResponse,
  ManualScaleRecommendation,
} from "@app/types";
import { createSelector } from "starfx";
import { normalizeInstanceClass, selectServiceById } from "../service";

export {
  RIGHTSIZED_AUTOSCALED_LABEL,
  SEGMENT_DISPLAY_LABELS,
  type SegmentState,
} from "./labels";

export interface DeployManualScaleRecommendationResponse {
  id: number;
  service_id: number;
  cpu_usage: number;
  ram_usage: number;
  ram_target: number;
  recommended_instance_class: string;
  recommended_container_memory_limit_mb: number;
  instance_class_at_time: string | null;
  container_memory_limit_mb_at_time: number;
  cost_savings: number;
  metric_percentile: number;
  created_at: string;
  updated_at: string;
  _links: {
    service: LinkResponse;
  };
  _type: "manual_service_sizing_recommendation";
}

export const defaultManualScaleRecommendationResponse = (
  s: Partial<DeployManualScaleRecommendationResponse> = {},
): DeployManualScaleRecommendationResponse => {
  const now = new Date().toISOString();
  return {
    id: 0,
    service_id: 0,
    cpu_usage: 0,
    ram_usage: 0,
    ram_target: 0,
    recommended_instance_class: "",
    recommended_container_memory_limit_mb: 0,
    instance_class_at_time: "",
    container_memory_limit_mb_at_time: 0,
    cost_savings: 0,
    metric_percentile: 0,
    created_at: now,
    updated_at: now,
    _links: {
      service: defaultHalHref(),
    },
    ...s,
    _type: "manual_service_sizing_recommendation",
  };
};

export const deserializeManualScaleRecommendation = (
  payload: DeployManualScaleRecommendationResponse,
): ManualScaleRecommendation => {
  return {
    id: `${payload.id}`,
    serviceId: `${payload.service_id}`,
    cpuUsage: payload.cpu_usage,
    ramUsage: payload.ram_usage,
    ramTarget: payload.ram_target,
    recommendedInstanceClass: payload.recommended_instance_class,
    recommendedContainerMemoryLimitMb:
      payload.recommended_container_memory_limit_mb,
    instanceClassAtTime: payload.instance_class_at_time ?? "",
    containerMemoryLimitMbAtTime: payload.container_memory_limit_mb_at_time,
    costSavings: payload.cost_savings,
    metricPercentile: payload.metric_percentile,
    createdAt: payload.created_at,
  };
};

export const manualScaleRecommendationEntities = {
  manual_service_sizing_recommendation: defaultEntity({
    id: "manual_service_sizing_recommendation",
    deserialize: deserializeManualScaleRecommendation,
    save: schema.manualScaleRecommendations.add,
  }),
};

export const selectManualScaleRecommendationByServiceId = createSelector(
  schema.manualScaleRecommendations.selectTableAsList,
  (_: WebState, p: { serviceId: string }) => p.serviceId,
  (recs, serviceId) =>
    recs.find((r) => {
      return r.serviceId === serviceId;
    }) || schema.manualScaleRecommendations.empty,
);

export const fetchManualScaleRecommendations = api.get(
  "/manual_service_sizing_recommendations?per_page=5000",
  { supervisor: cacheTimer() },
);

export const fetchManualScaleRecommendationById = api.get<{
  id: string;
}>("/manual_service_sizing_recommendations/:id");

// Compute whether a manual scaling recommendation should be shown as
// a scale up/down action, or hidden (none). Mirrors the UI rules in
// `src/ui/shared/scale-recs.tsx` so search tags and UI stay in sync.
export type ScaleRecommendationState = "scale_down" | "scale_up" | "none";

export interface MinimalServiceForRecEval {
  id: string;
  instanceClass: string;
  containerMemoryLimitMb: number;
  serviceSizingPolicyId: string;
}

export const deriveScaleRecommendationState = (
  service: MinimalServiceForRecEval | undefined,
  policy: { scalingEnabled: boolean } | undefined,
  recommendation: ManualScaleRecommendation | undefined,
): ScaleRecommendationState => {
  if (!service || !recommendation) return "none";

  // Hide recs when autoscaling is enabled
  if (policy?.scalingEnabled) return "none";

  if (!service.instanceClass || !recommendation.instanceClassAtTime) {
    return "none";
  }

  // Validate rec freshness and that the service hasn't changed since rec
  const hasProfileChanged =
    normalizeInstanceClass(service.instanceClass as InstanceClass) !==
    normalizeInstanceClass(recommendation.instanceClassAtTime as InstanceClass);
  const hasSizeChanged =
    service.containerMemoryLimitMb !==
    recommendation.containerMemoryLimitMbAtTime;
  const changedSinceRecommendation = hasProfileChanged || hasSizeChanged;
  const daysAgo = new Date();
  daysAgo.setDate(daysAgo.getDate() - 2);
  const withinTimelimit = new Date(recommendation.createdAt) > daysAgo;
  const isValid =
    !changedSinceRecommendation && withinTimelimit && recommendation.id !== "";
  if (!isValid) return "none";

  // Mirror UI display thresholds
  const savings = recommendation.costSavings;
  if (savings === 0) return "none";
  if (savings < 0) return "scale_up";
  if (savings > 0 && savings < 150) return "none";
  return "scale_down"; // savings >= 150
};

// Selector version for convenience in UI code
export const selectScaleRecommendationStateByServiceId = createSelector(
  selectServiceById,
  schema.manualScaleRecommendations.selectTableAsList,
  schema.serviceSizingPolicies.selectTable,
  (service, recs, policies): ScaleRecommendationState => {
    const recommendation = recs.find((r) => r.serviceId === service.id);
    const policy = policies[service.serviceSizingPolicyId];
    return deriveScaleRecommendationState(
      {
        id: service.id,
        instanceClass: service.instanceClass,
        containerMemoryLimitMb: service.containerMemoryLimitMb,
        serviceSizingPolicyId: service.serviceSizingPolicyId,
      },
      policy,
      recommendation,
    );
  },
);
